import React, { useEffect, useMemo, useState } from "react";
import { Alert, AutoComplete, Button, Divider, Empty, Form, Input, List, Space, Spin, Tag, Typography, message } from "antd";
import { SearchOutlined, SwapRightOutlined } from "@ant-design/icons";
import { useQuery } from "react-query";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

import { UTCTimePicker } from "../../../../components/DateTimeInput";
import AircraftSelector from "../../../../components/common/AircraftSelector";
import { referenceDataQueryKeys } from "../hooks/queryKeys";
import useScheduleDraftFlightFormOptions from "../hooks/useScheduleDraftFlightFormOptions";
import { getAddFlightRouteBlockTimeMinutes, inferAddFlightTimes } from "../lib/addFlightTimeUtils";
import { getRouteBlockTime, getRouteMinBlockTime } from "../lib/flightDataUtils";
import { normalizeAirportCode, routeMatchesCurrentAircraft } from "../lib/routeUtils";
import { scheduleDraftDataService } from "../services/scheduleDraftDataService";
import { scheduleDraftReferenceDataService } from "../services/scheduleDraftReferenceDataService";
import { DEFAULT_STC_TYPE, isRecommendedStcType, normalizeInheritedStcTypeValue, normalizeStcTypeEmptyValue, normalizeStcTypeValue, RECOMMENDED_STC_TYPE_OPTIONS, STC_CUSTOM_VALUE_WARNING } from "../constants/scheduleDraftOptions";

dayjs.extend(utc);

const { Text, Title } = Typography;

const ROUTE_CHAIN_SEPARATOR_REGEX = /[\s,，、;；|/\\\-–—>→]+/g;

function formatBlockTime(minutes) {
    const value = Number(minutes || 0);
    if (!Number.isFinite(value) || value <= 0) {
        return "BH N/A";
    }
    return `${Math.floor(value / 60)}h ${String(value % 60).padStart(2, "0")}m`;
}

function getRouteDisplayName(route) {
    return route?.route_name || `${route?.departure_airport || "--"}-${route?.arrival_airport || "--"}`;
}

function getLegKey(route) {
    const dep = normalizeAirportCode(route?.departure_airport);
    const arr = normalizeAirportCode(route?.arrival_airport);
    return dep && arr ? `${dep}:${arr}` : "";
}

function getRouteUsage(route, usageStats = {}) {
    const routeId = route?.id == null ? "" : String(route.id);
    return (routeId && usageStats.routeStatsById?.[routeId]) || usageStats.legStats?.[getLegKey(route)] || null;
}

function getRouteSearchText(route, usage) {
    return [
        route?.departure_airport,
        route?.arrival_airport,
        route?.route_name,
        route?.id,
        usage?.recentFlightNumber,
    ].filter(Boolean).join(" ").toLowerCase();
}

function compareRoutes(left, right, usageStats) {
    const leftUsage = getRouteUsage(left, usageStats);
    const rightUsage = getRouteUsage(right, usageStats);
    const leftRecent = leftUsage?.recentIndex ?? Number.POSITIVE_INFINITY;
    const rightRecent = rightUsage?.recentIndex ?? Number.POSITIVE_INFINITY;

    if (leftRecent !== rightRecent) {
        return leftRecent - rightRecent;
    }
    if ((leftUsage?.count || 0) !== (rightUsage?.count || 0)) {
        return (rightUsage?.count || 0) - (leftUsage?.count || 0);
    }
    if (Boolean(left?.is_default) !== Boolean(right?.is_default)) {
        return left?.is_default ? -1 : 1;
    }
    return getRouteDisplayName(left).localeCompare(getRouteDisplayName(right));
}

function buildFallbackFlightNumber(route, usage) {
    const candidates = [
        usage?.recentFlightNumber,
        route?.flight_number,
        route?.default_flight_number,
        route?.route_name,
        "NEW",
    ];
    return String(candidates.find((item) => String(item || "").trim().length > 0) || "NEW").trim().slice(0, 12).toUpperCase();
}

function buildChainFallbackFlightNumber(route, index) {
    return String(route?.flight_number || route?.default_flight_number || `NEW${index + 1}`).trim().slice(0, 12).toUpperCase();
}

function getSuggestedFlightNumber(response) {
    return String(response?.pairs?.[0]?.suggested_flight_number || "").trim().slice(0, 12).toUpperCase();
}

function buildSuggestionKey(aircraftReg, departureAirport, arrivalAirport) {
    const parts = [aircraftReg, departureAirport, arrivalAirport].map((item) => normalizeAirportCode(item));
    return parts.every(Boolean) ? parts.join(":") : "";
}

function buildSuggestionMap(response) {
    const suggestions = new Map();
    (response?.pairs || []).forEach((item) => {
        const key = buildSuggestionKey(item?.aircraft_reg, item?.dep, item?.arr);
        const flightNumber = String(item?.suggested_flight_number || "").trim().slice(0, 12).toUpperCase();
        if (key && flightNumber) {
            suggestions.set(key, flightNumber);
        }
    });
    return suggestions;
}

function getInsertionLabel(insertion = {}) {
    const anchor = insertion.anchorFlight || insertion.anchor_flight;
    const anchorLabel = anchor?.flight_number || `${anchor?.departure_airport || "--"}-${anchor?.arrival_airport || "--"}`;
    if (insertion.mode === "before" && anchor) {
        return `在 ${anchorLabel} 上方`;
    }
    if (insertion.mode === "after" && anchor) {
        return `在 ${anchorLabel} 下方`;
    }
    return "当前轨末尾";
}

function parseRouteChain(value) {
    const tokens = String(value || "")
        .trim()
        .toUpperCase()
        .replace(ROUTE_CHAIN_SEPARATOR_REGEX, " ")
        .split(" ")
        .map((token) => token.trim())
        .filter(Boolean);
    const invalidTokens = tokens.filter((token) => !/^[A-Z]{3}$/.test(token));
    const airports = tokens.filter((token) => /^[A-Z]{3}$/.test(token));
    const legs = airports.slice(0, -1).map((departureAirport, index) => ({
        departureAirport,
        arrivalAirport: airports[index + 1],
    }));

    return { airports, invalidTokens, legs };
}

function buildFallbackRoute(leg) {
    return {
        id: null,
        route_name: `${leg.departureAirport}-${leg.arrivalAirport}`,
        departure_airport: leg.departureAirport,
        arrival_airport: leg.arrivalAirport,
        block_time_minutes: null,
        is_custom: true,
    };
}

function buildFlightPayload({ route, timing, flightNumber, aircraftReg, trackId, index, total }) {
    const blockTimeMinutes = timing?.blockTimeMinutes ?? getAddFlightRouteBlockTimeMinutes(route);
    const routeMinBlockTimeMinutes = getRouteMinBlockTime(route);

    return {
        aircraft_reg: aircraftReg,
        flight_number: flightNumber,
        departure_airport: normalizeAirportCode(route?.departure_airport),
        arrival_airport: normalizeAirportCode(route?.arrival_airport),
        flight_route_id: route?.id || null,
        std: timing?.std ?? null,
        sta: timing?.sta ?? null,
        stc_type: DEFAULT_STC_TYPE,
        block_time_minutes: blockTimeMinutes ?? undefined,
        min_block_time_minutes: routeMinBlockTimeMinutes ?? undefined,
        is_fixed: false,
        track_id: trackId,
        _keep_add_flight_open: index < total - 1,
    };
}

function ensureChainTiming(timing, route) {
    if (timing?.std && timing?.sta) {
        return;
    }

    const departureAirport = normalizeAirportCode(route?.departure_airport) || "--";
    const arrivalAirport = normalizeAirportCode(route?.arrival_airport) || "--";
    throw new Error(`缺少 ${departureAirport}-${arrivalAirport} 的航路 block time 或机场最小过站时间，无法自动推导 STD/STA，请使用高级模式手动填写。`);
}

async function loadMatchedRoutes(legs, aircraftReg) {
    return Promise.all(legs.map(async (leg) => {
        try {
            const route = await scheduleDraftReferenceDataService.getBestMatchRoute(leg.departureAirport, leg.arrivalAirport, aircraftReg);
            return route || buildFallbackRoute(leg);
        } catch {
            return buildFallbackRoute(leg);
        }
    }));
}

const ScheduleDraftRoutePicker = ({
    draftId,
    draft,
    currentEditingTrackId,
    defaultAircraftReg,
    flights = [],
    insertionContext,
    airportConstraintsMap,
    onSubmit,
    onCancel,
    onAdvancedMode,
    confirmLoading,
}) => {
    const [form] = Form.useForm();
    const stcType = Form.useWatch("stc_type", form);
    const { aircrafts, aircraftMap, isLoading: loadingAircrafts } = useScheduleDraftFlightFormOptions(true);
    const [routeChainText, setRouteChainText] = useState("");
    const [searchQuery, setSearchQuery] = useState("");
    const [browseRoutesOpen, setBrowseRoutesOpen] = useState(false);
    const [selectedAircraftReg, setSelectedAircraftReg] = useState(defaultAircraftReg || "");
    const [selectedRoute, setSelectedRoute] = useState(null);
    const [timingSuggestion, setTimingSuggestion] = useState(null);
    const [loadingFlightNumber, setLoadingFlightNumber] = useState(false);
    const [addingChain, setAddingChain] = useState(false);

    useEffect(() => {
        if (defaultAircraftReg && !selectedAircraftReg) {
            setSelectedAircraftReg(defaultAircraftReg);
        }
    }, [defaultAircraftReg, selectedAircraftReg]);

    const parsedChain = useMemo(() => parseRouteChain(routeChainText), [routeChainText]);
    const routesQuery = useQuery(
        referenceDataQueryKeys.activeFlightRoutes(),
        () => scheduleDraftReferenceDataService.getActiveRoutes(),
        { enabled: browseRoutesOpen, staleTime: 5 * 60 * 1000, refetchOnWindowFocus: false },
    );
    const usageQuery = useQuery(
        referenceDataQueryKeys.flightRouteUsage(selectedAircraftReg),
        () => scheduleDraftReferenceDataService.getRouteUsageStats(selectedAircraftReg),
        { enabled: browseRoutesOpen && Boolean(selectedAircraftReg), staleTime: 5 * 60 * 1000, refetchOnWindowFocus: false },
    );

    const currentAircraft = aircraftMap?.[selectedAircraftReg] || null;
    const normalizedStcType = normalizeStcTypeValue(stcType);
    const showStcWarning = normalizedStcType !== "" && !isRecommendedStcType(normalizedStcType);
    const usageStats = usageQuery.data || { routeStatsById: {}, legStats: {} };
    const filteredRoutes = useMemo(() => {
        if (!browseRoutesOpen) {
            return [];
        }

        const normalizedQuery = searchQuery.trim().toLowerCase();
        return (routesQuery.data || [])
            .filter((route) => route?.is_active !== false)
            .filter((route) => routeMatchesCurrentAircraft(route, {
                aircraft_reg: selectedAircraftReg,
                aircraft_type: currentAircraft?.aircraft_type,
                aircraft_type_id: currentAircraft?.aircraft_type_id,
            }, { aircraftsByRegistration: aircraftMap }))
            .filter((route) => !normalizedQuery || getRouteSearchText(route, getRouteUsage(route, usageStats)).includes(normalizedQuery))
            .sort((left, right) => compareRoutes(left, right, usageStats));
    }, [aircraftMap, browseRoutesOpen, currentAircraft, routesQuery.data, searchQuery, selectedAircraftReg, usageStats]);

    const selectRoute = (route) => {
        const usage = getRouteUsage(route, usageStats);
        const timing = inferAddFlightTimes({
            flights,
            route,
            insertion: insertionContext,
            aircraftReg: selectedAircraftReg,
            draft,
            airportConstraintsMap,
        });
        const departureAirport = normalizeAirportCode(route?.departure_airport);
        const arrivalAirport = normalizeAirportCode(route?.arrival_airport);
        const fallbackFlightNumber = buildFallbackFlightNumber(route, usage);
        const routeBlockTimeMinutes = getAddFlightRouteBlockTimeMinutes(route);
        const routeMinBlockTimeMinutes = getRouteMinBlockTime(route);

        setSelectedRoute(route);
        setTimingSuggestion(timing);
        form.setFieldsValue({
            flight_number: fallbackFlightNumber,
            aircraft_reg: selectedAircraftReg,
            departure_airport: departureAirport,
            arrival_airport: arrivalAirport,
            flight_route_id: route?.id || null,
            block_time_minutes: routeBlockTimeMinutes ?? undefined,
            min_block_time_minutes: routeMinBlockTimeMinutes ?? undefined,
            std: timing?.std ?? null,
            sta: timing?.sta ?? null,
            stc_type: normalizeInheritedStcTypeValue(usage?.recentStcType),
            remarks: "",
        });

        if (draftId && selectedAircraftReg && departureAirport && arrivalAirport) {
            setLoadingFlightNumber(true);
            scheduleDraftDataService.getFlightNumberSuggestions(draftId, [{
                aircraft_reg: selectedAircraftReg,
                dep: departureAirport,
                arr: arrivalAirport,
            }]).then((response) => {
                const suggestion = getSuggestedFlightNumber(response);
                if (suggestion && form.getFieldValue("flight_number") === fallbackFlightNumber) {
                    form.setFieldsValue({ flight_number: suggestion });
                }
            }).finally(() => setLoadingFlightNumber(false));
        }
    };

    const buildChainPayloads = async () => {
        const matchedRoutes = await loadMatchedRoutes(parsedChain.legs, selectedAircraftReg);
        const suggestionResponse = draftId
            ? await scheduleDraftDataService.getFlightNumberSuggestions(draftId, parsedChain.legs.map((leg) => ({
                aircraft_reg: selectedAircraftReg,
                dep: leg.departureAirport,
                arr: leg.arrivalAirport,
            }))).catch(() => null)
            : null;
        const suggestions = buildSuggestionMap(suggestionResponse);
        const stagedFlights = [...flights];
        const payloads = [];

        if (insertionContext?.mode === "before" && insertionContext?.anchorFlight) {
            let nextAnchor = insertionContext.anchorFlight;
            for (let index = matchedRoutes.length - 1; index >= 0; index -= 1) {
                const route = matchedRoutes[index];
                const timing = inferAddFlightTimes({
                    flights: stagedFlights,
                    route,
                    insertion: { mode: "before", anchorFlight: nextAnchor },
                    aircraftReg: selectedAircraftReg,
                    draft,
                    airportConstraintsMap,
                });
                ensureChainTiming(timing, route);
                const flightNumber = suggestions.get(buildSuggestionKey(selectedAircraftReg, route.departure_airport, route.arrival_airport)) || buildChainFallbackFlightNumber(route, index);
                const payload = buildFlightPayload({ route, timing, flightNumber, aircraftReg: selectedAircraftReg, trackId: currentEditingTrackId, index, total: matchedRoutes.length });
                payloads.unshift(payload);
                nextAnchor = {
                    id: `chain-${index}`,
                    aircraft_reg: selectedAircraftReg,
                    departure_airport: payload.departure_airport,
                    arrival_airport: payload.arrival_airport,
                    std: payload.std,
                    sta: payload.sta,
                };
                stagedFlights.push(nextAnchor);
            }
            return payloads.map((payload, index) => ({ ...payload, _keep_add_flight_open: index < payloads.length - 1 }));
        }

        matchedRoutes.forEach((route, index) => {
            const timing = inferAddFlightTimes({
                flights: stagedFlights,
                route,
                insertion: index === 0 ? insertionContext : { mode: "end" },
                aircraftReg: selectedAircraftReg,
                draft,
                airportConstraintsMap,
            });
            ensureChainTiming(timing, route);
            const flightNumber = suggestions.get(buildSuggestionKey(selectedAircraftReg, route.departure_airport, route.arrival_airport)) || buildChainFallbackFlightNumber(route, index);
            const payload = buildFlightPayload({ route, timing, flightNumber, aircraftReg: selectedAircraftReg, trackId: currentEditingTrackId, index, total: matchedRoutes.length });
            payloads.push(payload);
            stagedFlights.push({
                id: `chain-${index}`,
                aircraft_reg: selectedAircraftReg,
                departure_airport: payload.departure_airport,
                arrival_airport: payload.arrival_airport,
                std: payload.std,
                sta: payload.sta,
            });
        });

        return payloads;
    };

    const handleAddRouteChain = async () => {
        if (!selectedAircraftReg) {
            message.warning("请先选择飞机");
            return;
        }
        if (parsedChain.invalidTokens.length) {
            message.warning(`无法识别机场码：${parsedChain.invalidTokens.join(", ")}`);
            return;
        }
        if (parsedChain.legs.length < 1) {
            message.warning("请输入至少两个机场码，例如 HKG NQZ LGG");
            return;
        }

        setAddingChain(true);
        try {
            const payloads = await buildChainPayloads();
            const customCount = payloads.filter((payload) => !payload.flight_route_id).length;
            let nextInsertionContext = insertionContext;
            for (const payload of payloads) {
                const addedFlight = await onSubmit({
                    ...payload,
                    ...(nextInsertionContext ? { insertionContext: nextInsertionContext } : {}),
                });
                if (nextInsertionContext?.mode === "after" && addedFlight?.id) {
                    nextInsertionContext = { mode: "after", anchorFlight: addedFlight };
                }
            }
            if (customCount > 0) {
                message.warning(`${customCount} 段未匹配到航路，已按自定义航段添加`);
            }
        } catch (error) {
            message.error(error?.response?.data?.detail || error?.message || "添加航段失败");
        } finally {
            setAddingChain(false);
        }
    };

    const handleStdChange = (value) => {
        if (!value) {
            return;
        }
        const blockTimeValue = form.getFieldValue("block_time_minutes") ?? timingSuggestion?.blockTimeMinutes;
        const blockTimeMinutes = Number(blockTimeValue);
        const std = dayjs.utc(value);
        if (!Number.isFinite(blockTimeMinutes) || !std.isValid()) {
            return;
        }
        form.setFieldsValue({ sta: std.add(blockTimeMinutes, "minute").toISOString() });
    };

    const handleConfirm = async () => {
        const values = await form.validateFields();
        await onSubmit({
            ...values,
            insertionContext,
            track_id: currentEditingTrackId || values.track_id,
            is_fixed: false,
            std: values.std?.toISOString ? values.std.toISOString() : values.std,
            sta: values.sta?.toISOString ? values.sta.toISOString() : values.sta,
            departure_airport: normalizeAirportCode(values.departure_airport),
            arrival_airport: normalizeAirportCode(values.arrival_airport),
            flight_route_id: values.flight_route_id || null,
            stc_type: normalizeStcTypeEmptyValue(values.stc_type),
        });
    };

    if (selectedRoute) {
        const blockTimeMinutes = form.getFieldValue("block_time_minutes") ?? timingSuggestion?.blockTimeMinutes;

        return (
            <div className="schedule-draft-route-picker">
                <Space direction="vertical" size={12} className="schedule-draft-route-picker-stack">
                    <div className="schedule-draft-route-confirm-summary">
                        <Space wrap size={[8, 6]}>
                            <Tag color="blue">{normalizeAirportCode(selectedRoute.departure_airport)} <SwapRightOutlined /> {normalizeAirportCode(selectedRoute.arrival_airport)}</Tag>
                            <Tag>{getRouteDisplayName(selectedRoute)}</Tag>
                            <Tag>{formatBlockTime(blockTimeMinutes)}</Tag>
                            {selectedRoute.is_custom && <Tag color="warning">自定义航段</Tag>}
                        </Space>
                        <Text type="secondary">插入位置：{getInsertionLabel(insertionContext)}</Text>
                    </div>
                    {timingSuggestion?.warnings?.length ? (
                        <Alert type="warning" showIcon message="时间已自动推导，但航段衔接不连续" description={timingSuggestion.warnings.join("；")} />
                    ) : timingSuggestion ? (
                        <Alert type="info" showIcon message="已根据插入位置自动推导 STD/STA" />
                    ) : (
                        <Alert type="warning" showIcon message="未自动推导 STD/STA" description="缺少航路 block time 或机场最小过站时间，请手动填写。" />
                    )}
                    <Form form={form} layout="vertical">
                        <Form.Item name="flight_number" label="航班号" rules={[{ required: true, message: "请输入航班号" }]}>
                            <Input maxLength={12} placeholder="例如 FM101" suffix={loadingFlightNumber ? <Spin size="small" /> : null} />
                        </Form.Item>
                        <Form.Item name="aircraft_reg" label="飞机" rules={[{ required: true, message: "请选择飞机" }]}>
                            <AircraftSelector
                                valueType="registration"
                                displayMode="detailed"
                                aircrafts={aircrafts}
                                loading={loadingAircrafts}
                                placeholder="选择飞机"
                                onChange={setSelectedAircraftReg}
                                style={{ width: "100%" }}
                            />
                        </Form.Item>
                        <Form.Item name="std" label="STD (UTC)" rules={[{ required: true, message: "请选择 STD" }]}>
                            <UTCTimePicker style={{ width: "100%" }} format="HH:mm" showDate={true} minuteStep={5} onChange={handleStdChange} />
                        </Form.Item>
                        <Form.Item name="sta" label="STA (UTC)" rules={[{ required: true, message: "请选择 STA" }]}>
                            <UTCTimePicker style={{ width: "100%" }} format="HH:mm" showDate={true} minuteStep={5} />
                        </Form.Item>
                        <Form.Item name="stc_type" label="飞行性质" normalize={normalizeStcTypeValue} validateStatus={showStcWarning ? "warning" : undefined} help={showStcWarning ? STC_CUSTOM_VALUE_WARNING : undefined}>
                            <AutoComplete
                                allowClear
                                options={RECOMMENDED_STC_TYPE_OPTIONS}
                                placeholder="输入或选择 STC"
                                filterOption={(inputValue, option) => String(option?.value || "").includes(normalizeStcTypeValue(inputValue))}
                            />
                        </Form.Item>
                        <Form.Item name="remarks" label="备注">
                            <Input.TextArea rows={3} maxLength={500} placeholder="可选" />
                        </Form.Item>
                        <Form.Item name="departure_airport" hidden><Input /></Form.Item>
                        <Form.Item name="arrival_airport" hidden><Input /></Form.Item>
                        <Form.Item name="flight_route_id" hidden><Input /></Form.Item>
                        <Form.Item name="block_time_minutes" hidden><Input /></Form.Item>
                        <Form.Item name="min_block_time_minutes" hidden><Input /></Form.Item>
                    </Form>
                    <Space className="schedule-draft-route-picker-footer">
                        <Button onClick={() => setSelectedRoute(null)}>返回快速添加</Button>
                        <Button onClick={onCancel}>取消</Button>
                        <Button type="primary" loading={confirmLoading} onClick={handleConfirm}>确认添加</Button>
                    </Space>
                </Space>
            </div>
        );
    }

    return (
        <div className="schedule-draft-route-picker">
            <Space direction="vertical" size={12} className="schedule-draft-route-picker-stack">
                <div className="schedule-draft-route-picker-meta">
                    <Title level={5}>快速添加航段</Title>
                    <Text type="secondary">插入位置：{getInsertionLabel(insertionContext)}</Text>
                    <AircraftSelector
                        allowClear
                        value={selectedAircraftReg || undefined}
                        valueType="registration"
                        displayMode="detailed"
                        aircrafts={aircrafts}
                        loading={loadingAircrafts}
                        placeholder="选择飞机"
                        onChange={(value) => setSelectedAircraftReg(value || "")}
                        style={{ width: 240 }}
                    />
                </div>
                <div className="schedule-draft-route-chain-box">
                    <Input.TextArea
                        autoFocus
                        value={routeChainText}
                        rows={3}
                        placeholder="输入机场串，例如：HKG NQZ LGG。支持空格、横杠、中文逗号、英文逗号等分隔。"
                        onChange={(event) => setRouteChainText(event.target.value.toUpperCase())}
                        onPressEnter={(event) => {
                            if (event.metaKey || event.ctrlKey) {
                                void handleAddRouteChain();
                            }
                        }}
                    />
                    {parsedChain.invalidTokens.length > 0 && (
                        <Alert type="warning" showIcon message={`无法识别机场码：${parsedChain.invalidTokens.join(", ")}`} />
                    )}
                    {parsedChain.legs.length > 0 && (
                        <Space wrap size={[6, 6]}>
                            {parsedChain.legs.map((leg) => (
                                <Tag key={`${leg.departureAirport}-${leg.arrivalAirport}`} color="blue">
                                    {leg.departureAirport} <SwapRightOutlined /> {leg.arrivalAirport}
                                </Tag>
                            ))}
                        </Space>
                    )}
                    <Space wrap>
                        <Button type="primary" loading={addingChain || confirmLoading} onClick={handleAddRouteChain}>
                            添加 {parsedChain.legs.length || ""} 段航班
                        </Button>
                        <Button onClick={() => setRouteChainText("HKG NQZ LGG")}>示例</Button>
                        <Button onClick={onCancel}>取消</Button>
                    </Space>
                </div>
                <Divider className="schedule-draft-route-picker-divider" />
                <Space wrap className="schedule-draft-route-picker-secondary-actions">
                    <Button onClick={() => setBrowseRoutesOpen((current) => !current)}>
                        {browseRoutesOpen ? "收起航路列表" : "浏览航路（可选，较慢）"}
                    </Button>
                    <Button type="link" className="schedule-draft-route-advanced-link" onClick={onAdvancedMode}>高级模式（手动填写）</Button>
                </Space>
                {browseRoutesOpen && (
                    <>
                        <Input
                            allowClear
                            prefix={<SearchOutlined />}
                            value={searchQuery}
                            placeholder="搜索航路、DEP、ARR 或历史航班号"
                            onChange={(event) => setSearchQuery(event.target.value)}
                        />
                        <Spin spinning={routesQuery.isLoading || usageQuery.isFetching}>
                            <List
                                className="schedule-draft-route-list"
                                dataSource={filteredRoutes}
                                locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="没有匹配航路" /> }}
                                renderItem={(route) => {
                                    const usage = getRouteUsage(route, usageStats);
                                    const blockTimeMinutes = getRouteBlockTime(route);
                                    return (
                                        <List.Item className="schedule-draft-route-list-item" onClick={() => selectRoute(route)}>
                                            <div className="schedule-draft-route-list-main">
                                                <Space wrap size={[8, 4]}>
                                                    <Text strong>{normalizeAirportCode(route.departure_airport)} <SwapRightOutlined /> {normalizeAirportCode(route.arrival_airport)}</Text>
                                                    <Tag>{formatBlockTime(blockTimeMinutes)}</Tag>
                                                    {route.is_default && <Tag color="blue">默认</Tag>}
                                                    {usage?.recentIndex === 0 && <Tag color="green">最近使用</Tag>}
                                                    {(usage?.count || 0) > 1 && <Tag color="gold">常用 {usage.count}</Tag>}
                                                </Space>
                                                <Text type="secondary">{getRouteDisplayName(route)}{usage?.recentFlightNumber ? ` · ${usage.recentFlightNumber}` : ""}</Text>
                                            </div>
                                        </List.Item>
                                    );
                                }}
                            />
                        </Spin>
                    </>
                )}
            </Space>
        </div>
    );
};

export default ScheduleDraftRoutePicker;
